import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Users, TrendingUp, Shield, AlertTriangle,
  PlayCircle, StopCircle, RefreshCw, DollarSign, MessageSquare,
  Loader2, HeartHandshake, Megaphone, ThumbsUp, ThumbsDown
} from "lucide-react";
import { APP_VERSION, APP_PUBLISHER } from "../lib/version";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LAYER_COLORS = { EXECUTIVE: "#002FA7", CONTROL: "#FF2A2A", OPERATIONS: "#00805A", INTELLIGENCE: "#B8860B", GROWTH: "#7000FF" };

const SENTIMENT_BADGE_COLORS = {
  positive: "bg-green-100 text-green-700",
  negative: "bg-red-100 text-red-700",
};
const DEFAULT_SENTIMENT_BADGE = "bg-gray-100 text-gray-600";

const ALERT_SEVERITY_DOT = {
  critical: "bg-red-500",
  high: "bg-orange-500",
};
const DEFAULT_ALERT_DOT = "bg-yellow-500";

const SentimentIcon = ({ sentiment }) => {
  if (sentiment === "positive") return <ThumbsUp className="w-4 h-4 text-green-600 mt-0.5" />;
  if (sentiment === "negative") return <ThumbsDown className="w-4 h-4 text-red-600 mt-0.5" />;
  return <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />;
};

const MetricCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className="bg-white border-2 border-gray-200 p-5" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-mono uppercase text-gray-500">{title}</p>
        <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Chivo, sans-serif', color }}>{value}</p>
        <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
      </div>
      {Icon && <Icon className="w-5 h-5" style={{ color }} />}
    </div>
  </div>
);

const AgentCard = ({ agent }) => (
  <div className="bg-white border-2 border-gray-200 p-4 flex items-center gap-4" data-testid={`agent-${agent.id}`}>
    <div className="w-10 h-10 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: LAYER_COLORS[agent.layer] || "#666" }}>
      {agent.id?.substring(0, 3)}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-bold text-sm">{agent.name}</p>
      <p className="text-xs text-gray-500">{agent.role}</p>
    </div>
    <div className="text-right">
      <span className={`text-[10px] font-mono px-1.5 py-0.5 ${agent.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{agent.active ? "ACTIVE" : "IDLE"}</span>
      <p className="text-[10px] font-mono text-gray-400 mt-1">{agent.layer}</p>
    </div>
  </div>
);

export const CompanyDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [agentStatus, setAgentStatus] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [growthSummary, setGrowthSummary] = useState(null);
  const [recentFeedback, setRecentFeedback] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [d, a, k, al, fb, gr, rfb] = await Promise.all([
        axios.get(`${API}/company/dashboard`), axios.get(`${API}/agents/status`),
        axios.get(`${API}/agents/kpis`), axios.get(`${API}/agents/alerts?limit=10`),
        axios.get(`${API}/feedback/summary`), axios.get(`${API}/agents/growth/summary`),
        axios.get(`${API}/feedback/recent?limit=5`)
      ]);
      setDashboard(d.data); setAgentStatus(a.data); setKpis(k.data.kpis || {});
      setAlerts(al.data.alerts || []); setFeedbackSummary(fb.data); setGrowthSummary(gr.data);
      setRecentFeedback(rfb.data.feedback || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[CompanyDashboard] fetch failed:", err?.message);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);

  // Memoize agent list to avoid recreating inline objects on every render
  const agentsList = useMemo(() => {
    if (!agentStatus?.agents) return [];
    return Object.entries(agentStatus.agents).map(([id, agent]) => ({ ...agent, id }));
  }, [agentStatus]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="company-dashboard">
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{APP_PUBLISHER.toUpperCase()} — COMPANY DASHBOARD</h1>
            <p className="text-sm text-gray-400 mt-1">Debate Engine v{APP_VERSION} — Autonomous Operations</p>
          </div>
          <button onClick={fetchData} className="p-2 hover:bg-gray-800 transition-colors" data-testid="refresh-btn"><RefreshCw className="w-5 h-5" /></button>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard title="Discussions Today" value={dashboard?.metrics?.discussions?.today || 0} subtitle={`${dashboard?.metrics?.discussions?.total || 0} total`} icon={MessageSquare} color="#002FA7" />
          <MetricCard title="Monthly Revenue" value={`EUR ${(dashboard?.metrics?.revenue?.monthly || 0).toFixed(2)}`} subtitle={`${dashboard?.metrics?.revenue?.premium_subscribers || 0} premium`} icon={DollarSign} color="#00805A" />
          <MetricCard title="User Satisfaction" value={feedbackSummary?.last_24h?.avg_score ? `${feedbackSummary.last_24h.avg_score}/5` : "—"} subtitle={`${feedbackSummary?.total_feedback || 0} feedback total`} icon={HeartHandshake} color="#7000FF" />
          <MetricCard title="Conversion Rate" value={growthSummary?.metrics?.premium_conversion_rate != null ? `${(growthSummary.metrics.premium_conversion_rate*100).toFixed(1)}%` : "—"} subtitle={`${growthSummary?.metrics?.discussions_today || 0} discussions today`} icon={Megaphone} color="#B8860B" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}><Users className="w-5 h-5" />AI Agents ({agentsList.length})</h2>
            <div className="space-y-2">
              {agentsList.map(agent => <AgentCard key={agent.id} agent={agent} />)}
              {agentsList.length === 0 && (
                <div className="bg-white border-2 border-gray-200 p-8 text-center"><Users className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No agents registered</p></div>
              )}
            </div>
            {growthSummary?.funnel && (
              <div className="mt-6">
                <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Megaphone className="w-5 h-5" />Growth Funnel</h2>
                <div className="bg-white border-2 border-gray-200 p-6">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div><p className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{growthSummary.funnel.total_sessions}</p><p className="text-xs font-mono text-gray-500 mt-1">SESSIONS</p></div>
                    <div><p className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{growthSummary.funnel.completed_discussions}</p><p className="text-xs font-mono text-gray-500 mt-1">COMPLETED</p></div>
                    <div><p className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{(growthSummary.funnel.completion_rate*100).toFixed(0)}%</p><p className="text-xs font-mono text-gray-500 mt-1">COMPLETION</p></div>
                    <div><p className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif', color: '#00805A' }}>{growthSummary.funnel.premium_conversions}</p><p className="text-xs font-mono text-gray-500 mt-1">PREMIUM</p></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><HeartHandshake className="w-5 h-5" />User Feedback</h2>
              <div className="bg-white border-2 border-gray-200">
                {recentFeedback.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {recentFeedback.map((fb, i) => (
                      <div key={fb._id || fb.discussion_id || `fb-${fb.timestamp}-${i}`} className="p-4 flex items-start gap-3" data-testid={`feedback-item-${i}`}>
                        <SentimentIcon sentiment={fb.sentiment} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold">{fb.score}/5</span>
                            <span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase ${SENTIMENT_BADGE_COLORS[fb.sentiment] || DEFAULT_SENTIMENT_BADGE}`}>{fb.sentiment}</span>
                          </div>
                          {fb.comment && <p className="text-sm text-gray-600 mt-1 truncate">{fb.comment}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="p-8 text-center text-gray-500"><HeartHandshake className="w-8 h-8 mx-auto mb-2 text-gray-300" />No feedback yet</div>}
              </div>
            </div>

            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><AlertTriangle className="w-5 h-5" />Alerts ({alerts.length})</h2>
              <div className="bg-white border-2 border-gray-200">
                {alerts.length > 0 ? alerts.slice(0, 5).map((a, i) => (
                  <div key={a.id || `${a.category}-${a.timestamp || i}`} className="p-4 border-b border-gray-200 last:border-0">
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${ALERT_SEVERITY_DOT[a.severity] || DEFAULT_ALERT_DOT}`} />
                      <div><p className="font-semibold text-sm">{a.category}</p><p className="text-sm text-gray-600">{a.message}</p></div>
                    </div>
                  </div>
                )) : <div className="p-8 text-center text-gray-500"><Shield className="w-8 h-8 mx-auto mb-2 text-gray-300" />No alerts</div>}
              </div>
            </div>

            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><TrendingUp className="w-5 h-5" />Agent KPIs</h2>
              <div className="bg-white border-2 border-gray-200">
                {kpis && Object.keys(kpis).length > 0 ? Object.entries(kpis).map(([agentId, agentKpis]) => (
                  <div key={agentId} className="p-4 border-b border-gray-200 last:border-0">
                    <p className="font-semibold text-sm mb-2">{agentId}</p>
                    {Array.isArray(agentKpis) && agentKpis.slice(0, 2).map((kpi) => (
                      <div key={`${agentId}-${kpi.kpi_name}`} className="flex justify-between text-xs text-gray-600">
                        <span>{kpi.kpi_name}</span>
                        <span className={`font-mono ${kpi.achievement_rate >= 1 ? 'text-green-600' : 'text-yellow-600'}`}>{typeof kpi.actual === 'number' ? kpi.actual.toFixed(2) : kpi.actual}</span>
                      </div>
                    ))}
                  </div>
                )) : <div className="p-8 text-center text-gray-500"><TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-300" />No KPI data</div>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
